import { useMemo, useState } from 'react';

const POR_PAGINA = 50;
const ETIQUETAS = {
  'UNIDAD MOVIL': 'Unidad móvil', 'SEDE TURBACO': 'Sede Turbaco', 'DOMICILIO': 'Domicilio',
  'NO CONTACTADA': 'No contactada', 'CONTACTADA': 'Contactada', 'NUMERO ERRADO': 'Número errado',
  'CONTESTA FAMILIAR': 'Contesta familiar', 'APTA': 'Apta', 'NO APTA': 'No apta',
  'URBANO': 'Urbano', 'RURAL': 'Rural',
};
const lindo = (s) => (s ? ETIQUETAS[s] || s : '—');
const fecha = (f) => (f ? f.slice(8, 10) + '/' + f.slice(5, 7) + '/' + f.slice(0, 4) : '—');

const COLS_CSV = ['documento', 'nombre', 'telefono', 'edad', 'zona', 'municipio', 'agente',
  'estado_contacto', 'fecha_gestion', 'aptitud', 'motivo_no_elegible', 'modalidad',
  'cita_fecha', 'cita_texto', 'estado_tamizaje'];
const celda = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",;\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

export default function Pacientes({ registros }) {
  const [busqueda, setBusqueda] = useState('');
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('');
  const [soloCita, setSoloCita] = useState(false);
  const [pagina, setPagina] = useState(1);

  const filtrados = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (registros || []).filter((r) => {
      if (ql && !(`${r.documento || ''}`.toLowerCase().includes(ql) || `${r.nombre || ''}`.toLowerCase().includes(ql))) return false;
      if (estado && r.estado_contacto !== estado) return false;
      if (soloCita && !r.tiene_cita) return false;
      return true;
    }).sort((a, b) => String(a.documento).localeCompare(String(b.documento)));
  }, [registros, q, estado, soloCita]);

  const total = filtrados.length;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  const pag = Math.min(pagina, paginas);
  const filas = filtrados.slice((pag - 1) * POR_PAGINA, pag * POR_PAGINA);

  function exportar() {
    const csv = '﻿' + [COLS_CSV.join(';'),
      ...filtrados.map((f) => COLS_CSV.map((c) => celda(f[c])).join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `registros_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1>Pacientes gestionados</h1>
          <p className="mut" style={{ marginTop: 6 }}>{total.toLocaleString('es-CO')} registros</p>
        </div>
        <button className="btn" onClick={exportar} disabled={!total}>Descargar CSV</button>
      </div>

      <form className="card" onSubmit={(e) => { e.preventDefault(); setPagina(1); setQ(busqueda); }}
            style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <div style={{ flex: '1 1 260px' }}>
          <label htmlFor="dbpo-q">Buscar por documento o nombre</label>
          <input id="dbpo-q" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="1234567890 o Martínez" />
        </div>
        <div style={{ flex: '0 1 220px' }}>
          <label htmlFor="dbpo-e">Estado del contacto</label>
          <select id="dbpo-e" value={estado} onChange={(e) => { setPagina(1); setEstado(e.target.value); }}>
            <option value="">Todos</option>
            {['CONTACTADA', 'NO CONTACTADA', 'NUMERO ERRADO', 'CONTESTA FAMILIAR'].map((v) => (
              <option key={v} value={v}>{lindo(v)}</option>
            ))}
          </select>
        </div>
        <label style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 10, fontWeight: 400 }}>
          <input type="checkbox" checked={soloCita} onChange={(e) => { setPagina(1); setSoloCita(e.target.checked); }} style={{ width: 16, height: 16 }} />
          Solo con cita asignada
        </label>
        <button className="primario" type="submit">Filtrar</button>
        <button className="btn" type="button" onClick={() => { setBusqueda(''); setQ(''); setEstado(''); setSoloCita(false); setPagina(1); }}>Limpiar</button>
      </form>

      <div className="card">
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Documento</th><th>Paciente</th><th>Teléfono</th><th className="n">Edad</th>
                <th>Zona</th><th>Agente</th><th>Estado</th><th>Gestión</th>
                <th>Aptitud</th><th>Modalidad</th><th>Cita</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((r) => (
                <tr key={r.documento}>
                  <td>{r.documento}</td>
                  <td>{r.nombre || '—'}</td>
                  <td>{r.telefono || '—'}</td>
                  <td className="n">{r.edad ?? '—'}</td>
                  <td>{lindo(r.zona)}</td>
                  <td>{r.agente || '—'}</td>
                  <td>
                    <span className={'pill ' + (r.estado_contacto === 'CONTACTADA' ? 'ok' : r.estado_contacto === 'NUMERO ERRADO' ? 'err' : 'low')}>
                      {lindo(r.estado_contacto)}
                    </span>
                  </td>
                  <td>{fecha(r.fecha_gestion)}</td>
                  <td>{lindo(r.aptitud)}</td>
                  <td>{lindo(r.modalidad)}</td>
                  <td>{r.cita_fecha ? fecha(r.cita_fecha) : (r.cita_texto ? lindo(r.cita_texto) : '—')}</td>
                </tr>
              ))}
              {!filas.length && (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: 26, color: 'var(--ink-3)' }}>
                  No hay registros con esos criterios.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, gap: 10, flexWrap: 'wrap' }}>
          <span className="mut">Página {pag} de {paginas}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {pag > 1 && <button className="btn" onClick={() => setPagina(pag - 1)}>Anterior</button>}
            {pag < paginas && <button className="btn" onClick={() => setPagina(pag + 1)}>Siguiente</button>}
          </div>
        </div>
      </div>

      <p className="mut" style={{ marginTop: 14 }}>
        Este listado contiene datos personales sensibles. Manéjelo conforme a la política de tratamiento de datos.
      </p>
    </>
  );
}
